<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRouter } from "vue-router";
import FlashcardDeck from "../components/FlashcardDeck.vue";
import { supabase } from "../lib/supabase.js";
import {
  getRaschOnboardingState,
  updateUserAbilityDistribution,
  appendUsedTestCardId,
  setOnboardingStartingRank,
  fetchNearestProbeCard,
  getFlashcardsByIds,
} from "../Scripts/dbFunctions.js";
import {
  updateAbilityPosterior,
  computeStartingWordRank,
} from "../lib/raschAdaptive.js";

const router = useRouter();
const deck = ref(null);
const enterBtnRef = ref(null);
const triggerEnterPressedEffect = () => {
  const el = enterBtnRef.value;
  if (!el) return;
  el.classList.add("pressed-effect");
  setTimeout(() => {
    el.classList.remove("pressed-effect");
  }, 120);
};
const handleEnterKeyDown = (e) => {
  if (
    e &&
    e.key === "Enter" &&
    deck.value &&
    deck.value.hasInput &&
    deck.value.hasInput()
  ) {
    triggerEnterPressedEffect();
  }
};

// Lock body scroll during onboarding test (robust via global class)
onMounted(() => {
  document.documentElement.classList.add("onboarding-no-scroll");
  document.body.classList.add("onboarding-no-scroll");

  // Animate the Enter/Learn button when Enter key submits via keyboard
  window.addEventListener("keydown", handleEnterKeyDown, { passive: true });
});
onBeforeUnmount(() => {
  document.documentElement.classList.remove("onboarding-no-scroll");
  document.body.classList.remove("onboarding-no-scroll");

  window.removeEventListener("keydown", handleEnterKeyDown);
});

// Config
const TOTAL_TEST_CARDS = 35; // Fixed length of onboarding phase

// UI/State
const isLoading = ref(true);
const error = ref("");
const activeLanguage = ref(null);

// Completion UI state
const isComplete = ref(false);
const finalStartingRank = ref(null);

// Rasch state
const mean = ref(0); // μ
const variance = ref(9); // λ²
const usedIds = ref([]); // array of used probe card ids (unique)
const currentCard = ref(null); // currently presented probe card
const preloadedCards = ref([]); // deck demo-mode cards; always [currentCard]
const deckKey = ref(0); // force remount after each response for adaptive selection
const deckLimit = computed(() => 1);

// Progress state
const completed = ref(0); // number of cards fully completed for this test (resumable)
const totalCount = ref(TOTAL_TEST_CARDS);

// Cards (replaced by Rasch adaptive flow)
// Note: We use `currentCard`, `preloadedCards = [currentCard]`, and `deckKey` to remount the deck.

// Derived UI metrics
const currentOrdinal = computed(() =>
  Math.min(completed.value + 1, totalCount.value)
);
const progressPercent = computed(() =>
  Math.round(((completed.value || 0) / (totalCount.value || 1)) * 100)
);

// Rasch math helpers are provided by ../lib/raschAdaptive.js

// (removed) Beta mean — not used in Rasch adaptive test

// (removed) Beta mode — not used in Rasch adaptive test

async function fetchActiveLanguage() {
  // Reads active_language from user_preferences for the current user
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth?.user?.id || null;
  if (!uid) throw new Error("Missing user id");
  const { data, error: prefErr } = await supabase
    .from("user_preferences")
    .select("active_language, starting_word_rank")
    .eq("user_id", uid)
    .maybeSingle();
  if (prefErr) {
    throw new Error(prefErr.message || "Failed to load user preferences");
  }
  const lang = data?.active_language || null;
  console.log("[OnboardingTest] fetchActiveLanguage", {
    lang,
    startingRank: data?.starting_word_rank?.[lang] ?? null,
  });
  return {
    lang,
    startingRank: data?.starting_word_rank?.[lang] ?? null,
  };
}

async function loadCardsForLanguage(lang) {
  console.log("[OnboardingTest] Rasch: load state and first probe", { lang });

  // Load baseline Rasch state (mean, var, used_ids)
  const { data: state, error: stateErr } = await getRaschOnboardingState(lang);
  if (stateErr) throw new Error(stateErr);

  mean.value = Number.isFinite(state?.mean) ? Number(state.mean) : 0;
  variance.value =
    Number.isFinite(state?.var) && state.var > 0 ? Number(state.var) : 9;
  usedIds.value = Array.isArray(state?.used_ids) ? state.used_ids.slice() : [];
  totalCount.value = TOTAL_TEST_CARDS;
  completed.value = Math.min(usedIds.value.length, totalCount.value);

  console.log("[OnboardingTest] Rasch initial state", {
    mean: mean.value,
    var: variance.value,
    completed: completed.value,
    used_ids_sample: usedIds.value.slice(0, 10),
  });

  // If already completed 50 unique probes, finalize immediately
  if (completed.value >= totalCount.value) {
    console.log("[OnboardingTest] Rasch: already completed; finalizing");
    await finalizeAndRedirect();
    return;
  }

  // Select the first probe: nearest normalized difficulty to current mean, every 5th id, skip used
  const { data: candidate, error: candErr } = await fetchNearestProbeCard(
    lang,
    mean.value,
    usedIds.value
  );
  if (candErr) {
    throw new Error(candErr?.message || "Failed to select initial probe");
  }
  if (!candidate) {
    error.value =
      "No eligible probe card available. Please try again later or switch languages.";
    console.warn(
      "[OnboardingTest] Rasch: no candidate found for initial probe",
      {
        lang,
        mean: mean.value,
        usedCount: usedIds.value.length,
      }
    );
    return;
  }

  // Load full flashcard details for the selected probe id
  try {
    const { data: cards, error: cardsErr } = await getFlashcardsByIds([
      candidate.id,
    ]);
    if (!cardsErr && Array.isArray(cards) && cards.length > 0) {
      currentCard.value = cards[0];
      preloadedCards.value = cards;
      console.log("[OnboardingTest] loaded full details for probe", {
        id: cards[0]?.id,
        fields: Object.keys(cards[0] || {}),
      });
    } else {
      console.warn(
        "[OnboardingTest] fallback to minimal candidate; details fetch failed",
        { error: cardsErr?.message || cardsErr, id: candidate.id }
      );
      currentCard.value = candidate;
      preloadedCards.value = [candidate];
    }
  } catch (e) {
    console.error("[OnboardingTest] exception fetching probe details", e);
    currentCard.value = candidate;
    preloadedCards.value = [candidate];
  }
  await nextTick();
  deckKey.value += 1;

  console.log("[OnboardingTest] Rasch: loaded first probe", {
    id: candidate.id,
    default_ranking: candidate.default_ranking,
  });
}

async function finalizeAndRedirect() {
  // Compute starting rank from posterior mean (de-normalized), clamped to [0, 5000]
  const rank = computeStartingWordRank(mean.value, 0, 5000);
  console.log("[OnboardingTest] Rasch finalize: computed starting rank", {
    mean: mean.value,
    var: variance.value,
    rank,
  });

  const lang = activeLanguage.value;
  if (!lang) {
    error.value = "Missing active language when finalizing";
    return;
  }

  const { success, error: setErr } = await setOnboardingStartingRank(
    lang,
    rank
  );
  if (!success) {
    error.value =
      (typeof setErr === "string" && setErr) ||
      setErr?.message ||
      "Failed to set starting rank";
    return;
  }

  // Show completion UI; also trigger FSRS scheduling via Edge Function (non-blocking)
  console.log("[OnboardingTest] starting rank saved; showing completion UI", {
    lang,
    rank,
  });
  try {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id || null;
    if (!uid) {
      console.warn("[OnboardingTest] skipping FSRS schedule: missing user id");
    } else {
      const payload = {
        user_id: uid,
        language_code: lang,
        starting_word_rank: rank,
        fsrs_defaults: { difficulty: 1, stability: 25, state: 3 },
      };
      supabase.functions
        .invoke("apply-fsrs-schedule", { body: payload })
        .then((res) => {
          console.log(
            "[OnboardingTest] Edge FSRS schedule result",
            res?.data ?? res
          );
        })
        .catch((err) => {
          console.error("[OnboardingTest] Edge FSRS schedule error", err);
        });
    }
  } catch (e) {
    console.error("[OnboardingTest] Edge FSRS schedule exception", e);
  }

  finalStartingRank.value = rank;
  isComplete.value = true;
}

async function handleCardReviewed(payload) {
  // Payload: { id, correct, durationMs }
  // Single-attempt mode: first attempt decides outcome; always advance and count one.
  try {
    const lang = activeLanguage.value;
    if (!lang) return;

    const card = currentCard.value;
    const cardId = payload?.id ?? card?.id ?? null;
    const correctFirstTry = payload?.correct === true;
    const x = correctFirstTry ? 1 : 0;

    console.log("[OnboardingTest] Rasch: card-reviewed", {
      payload,
      cardId,
      currentCardId: card?.id ?? null,
      mean_before: mean.value,
      var_before: variance.value,
      used_before: usedIds.value.length,
    });

    if (!card || !Number.isFinite(card?.default_ranking)) {
      console.error("[OnboardingTest] Rasch: missing current card context");
      return;
    }

    // Bayesian update via Newton–Raphson MAP + Laplace variance (frontend math)
    const res = updateAbilityPosterior({
      prior: { mean: mean.value, var: variance.value },
      deltaRaw: card.default_ranking,
      x,
      debug: true,
    });

    // Persist posterior to Supabase JSONB
    const { success: updOk, error: updErr } =
      await updateUserAbilityDistribution(
        res.posterior.mean,
        res.posterior.var,
        lang
      );
    if (!updOk) {
      console.error(
        "[OnboardingTest] Rasch: failed to persist ability distribution",
        updErr
      );
    }

    // Append used card id (unique) for resumability
    const { success: usedOk, error: usedErr } = await appendUsedTestCardId(
      lang,
      card.id
    );
    if (!usedOk) {
      console.error(
        "[OnboardingTest] Rasch: failed to append used test card id",
        usedErr
      );
    } else {
      if (!usedIds.value.includes(card.id)) usedIds.value.push(card.id);
      completed.value = Math.min(usedIds.value.length, totalCount.value);
    }

    // Update local state
    mean.value = res.posterior.mean;
    variance.value = res.posterior.var;

    console.log("[OnboardingTest] Rasch: posterior update", {
      cardId: card.id,
      default_ranking: card.default_ranking,
      response: x,
      mean_after: mean.value,
      var_after: variance.value,
      completed: completed.value,
      total: totalCount.value,
    });

    // Termination: stop after 50 unique probes
    if (completed.value >= totalCount.value) {
      await finalizeAndRedirect();
      return;
    }

    // Select next probe adaptively
    const { data: nextCard, error: nextErr } = await fetchNearestProbeCard(
      lang,
      mean.value,
      usedIds.value
    );
    if (nextErr) {
      console.error(
        "[OnboardingTest] Rasch: failed to select next probe",
        nextErr
      );
      // Do not halt UX; user can navigate away or retry
      return;
    }
    if (!nextCard) {
      console.warn("[OnboardingTest] Rasch: no next probe available");
      return;
    }

    // Load next probe into the deck (force remount)
    // Load full details for next probe
    try {
      const { data: cards, error: cardsErr } = await getFlashcardsByIds([
        nextCard.id,
      ]);
      if (!cardsErr && Array.isArray(cards) && cards.length > 0) {
        currentCard.value = cards[0];
        preloadedCards.value = cards;
        console.log("[OnboardingTest] loaded full details for next probe", {
          id: cards[0]?.id,
          fields: Object.keys(cards[0] || {}),
        });
      } else {
        console.warn(
          "[OnboardingTest] fallback to minimal nextCard; details fetch failed",
          { error: cardsErr?.message || cardsErr, id: nextCard.id }
        );
        currentCard.value = nextCard;
        preloadedCards.value = [nextCard];
      }
    } catch (e) {
      console.error(
        "[OnboardingTest] exception fetching next probe details",
        e
      );
      currentCard.value = nextCard;
      preloadedCards.value = [nextCard];
    }
    await nextTick();
    deckKey.value += 1;

    console.log("[OnboardingTest] Rasch: next probe selected", {
      id: nextCard.id,
      default_ranking: nextCard.default_ranking,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Error handling reviewed card (Rasch):", e);
  }
}

onMounted(async () => {
  console.log("[OnboardingTest] onMounted: initializing");
  try {
    isLoading.value = true;
    error.value = "";

    const { lang, startingRank } = await fetchActiveLanguage();
    console.log("[OnboardingTest] fetched active language", {
      lang,
      startingRank,
    });
    if (!lang) {
      error.value = "No active language set for this user.";
      return;
    }
    activeLanguage.value = lang;
    console.log("[OnboardingTest] active language set", { lang });

    // If onboarding already finalized, skip directly to Learn
    if (typeof startingRank === "number") {
      console.log(
        "[OnboardingTest] starting rank exists; redirecting to LearnView",
        { startingRank }
      );
      router.push("/learn");
      return;
    }

    await loadCardsForLanguage(lang);
    console.log("[OnboardingTest] loaded test cards", {
      totalCount: totalCount.value,
      completed: completed.value,
    });
  } catch (e) {
    error.value = e?.message || "Failed to initialize onboarding test";
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="onboarding-test-view">
    <div class="header">
      <h1>Proficiency Check.</h1>
    </div>

    <div v-if="error" class="error">
      {{ error }}
    </div>

    <div v-if="isLoading" class="loading">Loading test...</div>

    <template v-else>
      <div v-if="isComplete" class="completion">
        <h2>Assessment complete</h2>
        <p>
          We’ve finished your quick vocabulary assessment. You can start
          learning now.
        </p>
        <button class="start-learning-btn" @click="router.push('/learn')">
          Start learning
        </button>
      </div>

      <template v-else>
        <div class="progress-bar-container" aria-label="progress">
          <progress
            id="progress-bar"
            class="progress-bar"
            :value="completed"
            :max="totalCount"
          />
          <p class="progress-text">
            {{ currentOrdinal }} / {{ totalCount }} ({{ progressPercent }}%)
          </p>
        </div>

        <!-- Use FlashcardDeck in demo mode (preloadedCards), so FSRS and DB changes are not made by the deck -->
        <FlashcardDeck
          ref="deck"
          :key="deckKey"
          :preloaded-cards="preloadedCards"
          :limit="deckLimit"
          :auto-fetch="false"
          :show-state-dots="true"
          :show-morpho="true"
          :default-show-sentence-translation="true"
          :single-attempt="true"
          @card-reviewed="handleCardReviewed"
        />

        <!-- Bottom fixed bar with only the Enter / Learn Word button -->
        <div class="onboarding-bottom-bar">
          <div class="onboarding-bottom-bar-inner">
            <button
              type="button"
              class="btn btn-accent enter-btn"
              ref="enterBtnRef"
              @click="deck && deck.checkAnswer && deck.checkAnswer()"
              :aria-label="
                deck && deck.hasInput && deck.hasInput()
                  ? 'Enter word'
                  : 'Learn Word'
              "
              :title="
                deck && deck.hasInput && deck.hasInput()
                  ? 'Enter word'
                  : 'Learn Word'
              "
            >
              {{
                deck && deck.hasInput && deck.hasInput()
                  ? "Enter word"
                  : "Learn Word"
              }}
            </button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
.onboarding-test-view {
  display: flex;
  flex-direction: column;
  gap: 0px;
  max-width: 100%;
  padding: 1rem;
  max-height: calc(100vh - 200px);
}

:deep(.flashcard-container) {
  padding-left: 0;
  padding-right: 0;
}

.header {
  text-align: left;
  padding: 0.5rem 0.8rem;
  padding-left: 0px;
}

.header h1 {
  margin: 0;
  font-size: 1.6rem;
  color: var(--text-color, #2c3e50);
}

.header .sub {
  margin: 6px 0 0 0;
  color: #6b7280;
  font-size: 0.95rem;
  padding-left: 0px;
}

.loading {
  color: #6b7280;
  padding: 0.8rem;
}

.error {
  color: #d14343;
  background: #fde8e8;
  border: 1px solid #f8b4b4;
  padding: 10px 12px;
  border-radius: 8px;
  margin: 0.5rem 0;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.5rem 0.8rem;
  width: 100%;
  max-width: 650px;
  margin: 0 auto;
  box-sizing: border-box;
}

.progress-bar {
  flex: 1;
  min-width: 0;
  height: 12px;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
}

.progress-bar,
.progress-bar::-webkit-progress-bar {
  background-color: var(--hover-bg);
  border-radius: 8px;
}

.progress-bar::-webkit-progress-value {
  background-color: var(--accent);
  border-radius: 8px;
}
.progress-bar::-moz-progress-bar {
  background-color: var(--accent);
  border-radius: 8px;
}

.progress-text {
  margin: 0 8px;
  color: var(--text-color, #2c3e50);
  font-size: 0.95rem;
}

/* Prevent overlap with fixed left navbar on desktop */
.onboarding-test-view {
  --sidebar-width: 60px;
  margin-left: var(--sidebar-width); /* same width as .nav-bar */
  padding: 20px; /* consistent inner spacing */
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  box-sizing: border-box;
  padding-top: 35px;
  padding-bottom: calc(env(safe-area-inset-bottom) + 20px);
}

.onboarding-bottom-bar {
  height: 50px;
}

:deep(.flashcard-container) {
  padding-top: 10px;
}

/* Reset left margin on mobile where sidebar is off-canvas */
@media (max-width: 768px) {
  .onboarding-test-view {
    padding-left: 15px;
    padding-right: 15px;
    margin-left: 0;
  }
  .progress-bar-container {
    padding-left: 0px;
    padding-right: 0px;
    padding-top: 0;
    padding-bottom: 0;
    width: 90dvw;
    max-width: 650px;
    margin: 0 auto;
    box-sizing: border-box;
    margin-right: 0px;
  }
  .progress-bar {
    flex: 1;
    min-width: 0;
  }
  :deep(.flashcard-container) {
    padding: 0px;
  }
  .header h1 {
    font-size: 1.3rem;
  }
  :deep(.sentence-blank) {
    margin-bottom: 20px !important;
    padding-bottom: 0 !important;
  }
}

.completion {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 70vh;
  padding: 2rem;
}

.completion h2 {
  font-size: 2.2rem;
  margin: 0 0 0.75rem 0;
  color: var(--text-color, #2c3e50);
}

.completion p {
  font-size: 1.15rem;
  color: #6b7280;
  max-width: 720px;
  margin: 0 0 1.25rem 0;
}

.start-learning-btn {
  width: auto;
  padding: 1rem 1.6rem;
  font-size: 1.1rem;
  background-color: #8ad1a9;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.start-learning-btn:hover {
  filter: brightness(0.95);
}

/* Onboarding bottom bar (button-only) */
.onboarding-bottom-bar {
  position: fixed;
  bottom: calc(25px + env(safe-area-inset-bottom));
  left: var(--sidebar-width, 60px);
  right: 0;
  z-index: 50;
  pointer-events: none; /* allow clicks only on inner content */
}

.onboarding-bottom-bar-inner {
  background: var(--hover-bg);
  color: var(--text-color);
  border-radius: 13px;
  box-sizing: border-box;
  /*padding: 0 10px;*/
  box-shadow: none;
  padding-left: max(8px, env(safe-area-inset-left));
  padding-right: max(8px, env(safe-area-inset-right));
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  width: min(650px, calc(100vw - var(--sidebar-width, 60px)));
  margin: 0 auto;
  width: 100%;
  max-width: 650px;
  min-height: 44px;
  z-index: 50;
  box-sizing: border-box;
  height: 50px;
  pointer-events: auto;
}
/* removed stray duplicated styles */

/* Responsive tweaks for mobile */
@media (max-width: 768px) {
  .onboarding-bottom-bar {
    bottom: calc(15px + env(safe-area-inset-bottom));
    left: 0;
    right: 0;
  }
  .onboarding-bottom-bar-inner {
    width: 90dvw;
    max-width: 650px;
    margin: 0 auto;
  }
}

/* Minimal local button styles (match Learn look-and-feel) */
.btn {
  --btn-bg: var(--hover-bg, #f1f3f5);
  --btn-fg: var(--text-color, #2c3e50);
  --btn-br: 10px;
  --btn-pad: 10px 14px;

  background: var(--btn-bg);
  color: var(--btn-fg);
  border: none;
  border-radius: var(--btn-br);
  padding: var(--btn-pad);
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0px;
}

.btn-accent {
  --btn-bg: var(--accent, #8ad1a9);
  --btn-fg: #fff;
}

.enter-btn {
  font-weight: 400;
  align-self: center;
  margin-left: auto;
  white-space: nowrap;
  transition: transform 0.06s ease, background 0.15s ease, box-shadow 0.15s ease,
    filter 0.15s ease;
}

.enter-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px color-mix(in oklab, var(--btn-bg) 40%, transparent);
  filter: saturate(1.05);
}
.enter-btn:active:not(:disabled) {
  transform: translateY(0);
  background: color-mix(in oklab, var(--btn-bg) 90%, black 10%);
  filter: saturate(0.98);
}
.enter-btn.pressed-effect {
  transform: translateY(0);
  background: color-mix(in oklab, var(--btn-bg) 90%, black 10%);
  filter: saturate(0.98);
}
/* Show keyboard return icon on desktop to the right of the text */
@media (min-width: 769px) {
  .enter-btn::after {
    content: "";
    display: inline-block;
    width: 18px;
    height: 18px;
    margin-left: 8px;
    background: url("../assets/KeyboardReturnIcon.svg") no-repeat center center /
      contain;
    vertical-align: middle;
  }
}
</style>
<style>
/* Global scroll lock for onboarding test (overrides inline changes elsewhere) */
.onboarding-no-scroll {
  overflow: hidden !important;
  height: 100dvh !important;
}
.onboarding-no-scroll #app {
  height: 100dvh !important;
  overflow: hidden !important;
}
</style>
